import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Seo } from "@/components/Seo";
import { Reveal, SectionHead, Panel, Tag, Dot } from "@/components/System";
import { PROOF_LAYERS } from "@/data/content";

const Proof = () => {
    const [active, setActive] = useState("identity");
    const layer = PROOF_LAYERS.find((l) => l.id === active);

    return (
        <div>
            <Seo path="/proof" />
            <section className="relative overflow-hidden px-4 pb-16 pt-36 sm:px-6">
                <div className="vm-grid-bg pointer-events-none absolute inset-0 opacity-30" aria-hidden="true" />
                <div className="relative mx-auto max-w-7xl">
                    <div className="vm-micro mb-6">AGENTVM://PROOF_CONSOLE</div>
                    <h1 className="max-w-4xl text-4xl font-bold tracking-tight text-[#F5F9FF] sm:text-6xl">
                        DON'T TRUST THE AGENT.
                        <br />
                        <span className="text-[#00D9FF] vm-glow">VERIFY IT.</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#B8C7D9] sm:text-lg">
                        The AgentVM proof model answers five questions about every execution. Select a layer to inspect
                        what it proves, how it works, and why it matters.
                    </p>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6" data-testid="proof-console">
                <div className="grid gap-5 lg:grid-cols-[0.9fr_1.3fr]">
                    <div className="flex flex-col gap-3">
                        {PROOF_LAYERS.map((l, i) => {
                            const isActive = l.id === active;
                            return (
                                <Reveal key={l.id} delay={i * 0.06}>
                                    <button
                                        data-testid={`proof-layer-${l.id}`}
                                        onClick={() => setActive(l.id)}
                                        aria-pressed={isActive}
                                        className={`group w-full border px-5 py-4 text-left transition-all duration-300 ${
                                            isActive
                                                ? "border-[#00D9FF]/60 bg-[#00D9FF]/[0.07] shadow-[0_0_30px_rgba(0,217,255,0.08)]"
                                                : "border-[rgba(0,217,255,0.12)] bg-[#080F18]/50 hover:border-[#00D9FF]/35"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between gap-4">
                                            <div className="flex items-center gap-4">
                                                <span className={`font-mono text-xs tracking-[0.2em] ${isActive ? "text-[#00D9FF]" : "text-[#B8C7D9]/50"}`}>
                                                    {l.num}
                                                </span>
                                                <div>
                                                    <div className={`font-mono text-sm tracking-[0.14em] ${isActive ? "text-[#F5F9FF]" : "text-[#B8C7D9] group-hover:text-[#F5F9FF]"}`}>
                                                        {l.name}
                                                    </div>
                                                    <div className="vm-micro-dim mt-1">{l.question}</div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <Tag tone={l.tone}>{l.status}</Tag>
                                                {isActive && <Dot />}
                                            </div>
                                        </div>
                                    </button>
                                </Reveal>
                            );
                        })}
                    </div>

                    <div className="lg:sticky lg:top-24 lg:self-start">
                        <Panel title={`PROOF://LAYER_${layer.num}`} right={layer.question} className="scanlines" testid="proof-detail">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={layer.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.3 }}
                                    className="p-6 sm:p-8"
                                >
                                    <h2 className="font-mono text-xl tracking-[0.12em] text-[#F5F9FF] sm:text-2xl">{layer.name}</h2>
                                    <div className="mt-6 space-y-6">
                                        {[
                                            ["DESCRIPTION", layer.desc],
                                            ["TECHNICAL MEANING", layer.tech],
                                            ["WHY IT MATTERS", layer.why],
                                        ].map(([label, text]) => (
                                            <div key={label}>
                                                <div className="vm-micro mb-2">{label}</div>
                                                <p className="text-sm leading-relaxed text-[#B8C7D9]">{text}</p>
                                            </div>
                                        ))}
                                        <div>
                                            <div className="vm-micro mb-2">SOURCE</div>
                                            <a
                                                href={layer.source.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                data-testid={`proof-source-${layer.id}`}
                                                className="font-mono text-xs tracking-[0.1em] text-[#00D9FF] hover:underline"
                                            >
                                                {layer.source.label} ↗
                                            </a>
                                        </div>
                                        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-[rgba(0,217,255,0.12)] pt-5">
                                            <div>
                                                <div className="vm-micro mb-1.5">VERIFICATION STATUS</div>
                                                <Tag tone={layer.tone}>{layer.status}</Tag>
                                            </div>
                                            <div className="text-right">
                                                <div className="vm-micro mb-1.5">SAMPLE</div>
                                                <span className="font-mono text-[10px] tracking-[0.14em] text-[#B8C7D9]/60">{layer.demoHash}</span>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                        </Panel>
                    </div>
                </div>
            </section>

            <section className="border-t border-[rgba(0,217,255,0.1)] bg-[#080F18]/40 py-20">
                <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
                    <Reveal>
                        <p className="text-2xl font-semibold leading-snug text-[#F5F9FF] sm:text-3xl">
                            FIVE LAYERS. <span className="text-[#00D9FF]">ONE VERIFIABLE RUN.</span>
                        </p>
                        <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-[#B8C7D9]">
                            Identity says who. Evidence says what. Attestation says where. Bindings say which code.
                            GPU attestation says which hardware. Together, they make execution independently
                            verifiable.
                        </p>
                    </Reveal>
                </div>
            </section>
        </div>
    );
};

export default Proof;
